import Image from "next/image";
import VerticalLinearStepper from "./Stepper";
import CustomizationIcon from "@modules/common/icons/customization";
import { useIsElementCentered } from "@lib/hooks/use-in-screen-center";
import { useEffect, useRef, useState } from "react";
import { Step } from "types/homepage";
import { useTranslation } from "@i18n";

export enum StepsEnum {
  WelcomeOffer = "Welcome Offer Redemption",
  CheckTheMenu = "Menu Browsing",
  AddYourItems = "Item Selection",
  PaymentOptions = "Payment Options",
  Checkout = "Checkout",
  Success = "Order Success and Points Collection",
  Reviews = "Order Reviews",
}

export enum Colors {
  Color1 = "#AE3868",
  Color2 = "#265940",
  Color3 = "#4A085A",
}

// Phone-preview GIFs are flat-listed in (step × color) order so the existing
// `slideIndex = stepIdx * 3 + colorIdx` math keeps working.
// Color order: 0 = Pink (Color1), 1 = Green (Color2), 2 = Purple (Color3).
const PHONE_GIFS_EN: readonly string[] = [
  "/gifs/Pink-WO.gif", "/gifs/Green-WO.gif", "/gifs/Purple-WO.gif",
  "/gifs/Pink-menu.gif", "/gifs/Green-menu.gif", "/gifs/Purple-menu.gif",
  "/gifs/Pink-items.gif", "/gifs/Green-items.gif", "/gifs/Purple-items.gif",
  "/gifs/Pink-Pay.gif", "/gifs/Green-Pay.gif", "/gifs/Purple-Pay.gif",
  "/gifs/Pink-Cart.gif", "/gifs/Green-Cart.gif", "/gifs/Purple-Cart.gif",
  "/gifs/Pink-success.gif", "/gifs/Green-success.gif", "/gifs/Purple-success.gif",
  "/gifs/Pink-reviews.gif", "/gifs/Green-reviews.gif", "/gifs/Purple-reviews.gif",
];

// Arabic filenames are inconsistent (spacing, casing, even a "Pay..gif"
// double-dot) — preserved verbatim because renaming would hide the source.
const PHONE_GIFS_AR: readonly string[] = [
  "/gifs/arabic-gifs/1 - Pink - Arabic - WO.gif",
  "/gifs/arabic-gifs/1 - Green - Arabic -WO.gif",
  "/gifs/arabic-gifs/1- Purple - Arabic - WO.gif",
  "/gifs/arabic-gifs/2- Pink - Arabic - Menu.gif",
  "/gifs/arabic-gifs/2 - Green - Arabic - Menu.gif",
  "/gifs/arabic-gifs/2 - Purple - Arabic - menu.gif",
  "/gifs/arabic-gifs/3 - Pink - Arabic - Item.gif",
  "/gifs/arabic-gifs/3 - Green - Arabic - Item.gif",
  "/gifs/arabic-gifs/3 - Purple - Arabic -item.gif",
  "/gifs/arabic-gifs/4 - Pink - Arabic - Pay.gif",
  "/gifs/arabic-gifs/4 - Green - Arabic - Pay.gif",
  "/gifs/arabic-gifs/4 - Purple - Arabic - Pay..gif",
  "/gifs/arabic-gifs/5 - Pink - Arabic - Cart.gif",
  "/gifs/arabic-gifs/5 - Green - Arabic - Cart.gif",
  "/gifs/arabic-gifs/5 - Purple - Arabic - Cart.gif",
  "/gifs/arabic-gifs/6 - Pink - Arabic - success.gif",
  "/gifs/arabic-gifs/6 - Green - Arabic - Success.gif",
  "/gifs/arabic-gifs/6 - Purple - Arabic - success.gif",
  "/gifs/arabic-gifs/7 - Pink - Arabic - Review.gif",
  "/gifs/arabic-gifs/7 - Green - Arabic - Review.gif",
  "/gifs/arabic-gifs/7 - purple - Arabic - review.gif",
];

const STEP_COUNT = 7;

function Customization({
  data,
}: {
  data: {
    customerSectionTitle: string;
    customerSectionText: string;
    customerSectionChooseColorText: string;
    customerSectionChooseColorTitle: string;
    steps: Step[];
  };
}) {
  void data;
  const elementRef = useRef<HTMLDivElement>(null);
  const isCentered = useIsElementCentered(elementRef);
  const [step, setStep] = useState(0);
  const [color, setColor] = useState(Colors.Color1);
  const { t, locale } = useTranslation();

  useEffect(() => {
    if (!elementRef.current) {
      console.error("The ref is null. Make sure it's attached to an element.");
      return;
    }
  }, [elementRef]);

  const ColorIndex = {
    [Colors.Color1]: 0,
    [Colors.Color2]: 1,
    [Colors.Color3]: 2,
  };

  const phoneGifs = locale === "ar" ? PHONE_GIFS_AR : PHONE_GIFS_EN;
  const stepperSteps: Step[] = Array.from({ length: STEP_COUNT }, (_, i) => ({
    id: i,
    name: t.customization.steps[i] ?? `Step ${i + 1}`,
    // Image fields are no longer used for rendering — Stepper only reads id/name.
    imageColor1: { url: "" } as Step["imageColor1"],
    imageColor2: { url: "" } as Step["imageColor2"],
    imageColor3: { url: "" } as Step["imageColor3"],
  }));

  return (
    <div
      ref={elementRef}
      className={`flex w-screen px-4 lg:px-[4.5rem]  py-10 h-0 min-h-0 flex-col lg:flex-row gap-10 border-primary border-2 rounded-2xl lg:rounded-[70px] transition-all duration-[2s]  justify-center ${
        isCentered
          ? "py-4 lg:py-32 !min-h-[96vh] h-[125vh] lg:h-[96vh] !lg:max-h-[96vh] "
          : ""
      } overflow-clip`}
    >
      <div className="items-center lg:items-start w-full lg:w-1/2 h-auto  text-darkGreen content-center lg:h-auto lg:me-10 flex flex-col justify-between gap-10">
        <div className="flex flex-col justify-items-center items-center justify-center lg:justify-start lg:items-start ">
          <p className="text-sm lg:text-base border-[1px] border-primary  text-darkGreen px-2 py-0.5 rounded-md w-fit mb-6">
            {t.customization.sectionTitle}
          </p>
          <h2 className=" lg:text-[4vw] lg:text-6xl text-3xl lg:text-start text-center">
            {t.customization.titleLine1Prefix}{" "}
            <span className="text-primary">
              {t.customization.titleLine1Highlight}
            </span>
            {t.customization.titleLine2 && (
              <>
                <br />
                {t.customization.titleLine2}
              </>
            )}
            {t.customization.titleLine3 && (
              <>
                <br />
                {t.customization.titleLine3}
              </>
            )}
          </h2>
        </div>

        <div className="max-w-[80%] lg:h-[200px] lg:w-[300px] bg-lightPrimary text-darkGreen rounded-xl px-4 py-6 flex gap-4 mt-8">
          <CustomizationIcon size={70} />
          <div className="flex flex-col justify-between gap-2">
            <h3 className="text-lg font-semibold leading-5">
              {t.customization.chooseColorTitle}
            </h3>
            <p className=" text-xs">{t.customization.chooseColorText}</p>
            <div className="flex gap-2">
              {Object.values(Colors).map((mappedColor) => {
                return (
                  <div
                    key={mappedColor}
                    className={` border-lightPrimary border-[1px] rounded-full cursor-pointer transition-all duration-500  hover:border-midPrimary ${
                      color === mappedColor ? " !border-darkGreen " : ""
                    } `}
                    onClick={() => setColor(mappedColor)}
                  >
                    <div
                      className={`border-[3px] border-lightPrimary h-8 w-8 rounded-full`}
                      style={{ backgroundColor: mappedColor }}
                    ></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full lg:w-1/2 relative lg:h-auto flex">
        <div
          className="lg:h-[64.4vh] lg:min-w-[30.8vh]  rounded-2xl border-darkGreen bg-darkGreen border-8 overflow-clip
        min-w-[50vw] h-[103.5vw] self-center relative
        "
        >
          {phoneGifs.map((src, slideIndex) => {
            const activeIndex = step * 3 + ColorIndex[color];
            const isActive = slideIndex === activeIndex;
            return (
              <Image
                key={src}
                src={src}
                alt={`Step ${Math.floor(slideIndex / 3) + 1} color ${(slideIndex % 3) + 1}`}
                fill
                unoptimized
                sizes="(max-width: 1024px) 50vw, 30vw"
                style={{
                  objectFit: "cover",
                  opacity: isActive ? 1 : 0,
                  transition: "opacity 0.4s ease-in-out",
                  pointerEvents: isActive ? "auto" : "none",
                }}
                loading={slideIndex === 0 ? "eager" : "lazy"}
              />
            );
          })}
        </div>
        <div className="lg:h-[64.4vh] h-[103.5vw] w-[300px] self-center rounded-xl ps-6 lg:ps-10 ">
          <VerticalLinearStepper
            step={step}
            setStep={setStep}
            steps={stepperSteps}
            stepNames={t.customization.steps}
          />
        </div>
      </div>
    </div>
  );
}

export default Customization;
